import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    ComposableMap,
    Geographies,
    Geography,
    Graticule,
    Marker,
    ZoomableGroup,
    Line,
} from 'react-simple-maps';
import { RefreshCw, ZoomIn, ZoomOut, RotateCcw, Search, MapPin, Crosshair, Route } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

// World topology URLs - use higher resolution for zoomed view
const GEO_URL_LOW = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const GEO_URL_HIGH = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";
// States/provinces for detailed view
const GEO_URL_US_STATES = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const GEO_URL_AU_STATES = "https://raw.githubusercontent.com/rowanhogan/australian-states/master/states.geojson";

// Major airport coordinates for calculating plane orientation toward destination
const AIRPORT_COORDS = {
    // Australia
    'SYD': [-33.9399, 151.1753], 'MEL': [-37.6690, 144.8410], 'BNE': [-27.3842, 153.1175],
    'PER': [-31.9403, 115.9670], 'ADL': [-34.9450, 138.5306], 'OOL': [-28.1644, 153.5047],
    'CNS': [-16.8858, 145.7553], 'DRW': [-12.4147, 130.8769], 'CBR': [-35.3069, 149.1950],
    'HBA': [-42.8361, 147.5103], 'TSV': [-19.2525, 146.7656], 'AYQ': [-25.1861, 130.9756],
    // New Zealand
    'AKL': [-37.0082, 174.7850], 'WLG': [-41.3272, 174.8050], 'CHC': [-43.4894, 172.5322],
    'ZQN': [-45.0211, 168.7392], 'DUD': [-45.9281, 170.1983],
    // USA
    'JFK': [40.6413, -73.7781], 'LAX': [33.9425, -118.4081], 'ORD': [41.9742, -87.9073],
    'DFW': [32.8998, -97.0403], 'DEN': [39.8561, -104.6737], 'SFO': [37.6213, -122.3790],
    'SEA': [47.4502, -122.3088], 'ATL': [33.6407, -84.4277], 'MIA': [25.7959, -80.2870],
    'BOS': [42.3656, -71.0096], 'LAS': [36.0840, -115.1537], 'PHX': [33.4373, -112.0078],
    // Europe
    'LHR': [51.4700, -0.4543], 'CDG': [49.0097, 2.5479], 'FRA': [50.0379, 8.5622],
    'AMS': [52.3105, 4.7683], 'MAD': [40.4983, -3.5676], 'FCO': [41.8003, 12.2389],
    'MUC': [48.3538, 11.7861], 'ZRH': [47.4647, 8.5492], 'VIE': [48.1103, 16.5697],
    // Asia
    'NRT': [35.7720, 140.3929], 'HND': [35.5494, 139.7798], 'HKG': [22.3080, 113.9185],
    'SIN': [1.3644, 103.9915], 'ICN': [37.4602, 126.4407], 'BKK': [13.6900, 100.7501],
    'KIX': [34.4320, 135.2304], 'PEK': [40.0799, 116.6031], 'PVG': [31.1443, 121.8083],
    'DEL': [28.5562, 77.1000], 'BLR': [13.1986, 77.7066], 'MNL': [14.5086, 121.0194],
    'FUK': [33.5859, 130.4511], 'CTS': [42.7752, 141.6925],
    // Middle East
    'DXB': [25.2532, 55.3657], 'DOH': [25.2609, 51.6138], 'AUH': [24.4330, 54.6511],
    // Canada
    'YYZ': [43.6777, -79.6248], 'YVR': [49.1967, -123.1815], 'YUL': [45.4706, -73.7408],
};

// Calculate bearing from current position to destination airport
const calculateBearingToDestination = (lat, lon, arrivalIata) => {
    const destCoords = AIRPORT_COORDS[arrivalIata];
    if (!destCoords) return null;
    
    const [destLat, destLon] = destCoords;
    
    // Convert to radians
    const lat1 = lat * Math.PI / 180;
    const lat2 = destLat * Math.PI / 180;
    const dLon = (destLon - lon) * Math.PI / 180;
    
    // Calculate bearing
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    let bearing = Math.atan2(y, x) * 180 / Math.PI;
    
    // Normalize to 0-360
    bearing = (bearing + 360) % 360;
    
    return bearing;
};

// Aircraft type categories
const LARGE_AIRCRAFT_LIST = [
    'A332', 'A333', 'A338', 'A339', 'A342', 'A343', 'A345', 'A346', 
    'A359', 'A35K', 'A380', 'A388', 'A310', 'A306', 'A30B',
    'B741', 'B742', 'B743', 'B744', 'B748', 'B762', 'B763', 'B764',
    'B772', 'B773', 'B77L', 'B77W', 'B778', 'B779',
    'B781', 'B788', 'B789', 'B78X',
    'MD11', 'DC10', 'IL96', 'IL86',
    'A318', 'A319', 'A320', 'A321', 'A20N', 'A21N',
    'B731', 'B732', 'B733', 'B734', 'B735', 'B736', 'B737', 'B738', 'B739',
    'B37M', 'B38M', 'B39M', 'B752', 'B753',
    'BCS1', 'BCS3', 'E190', 'E195', 'E290', 'E295',
];

const MEDIUM_AIRCRAFT_LIST = [
    'E170', 'E175', 'E75L', 'E75S',
    'CRJ1', 'CRJ2', 'CRJ7', 'CRJ9', 'CRJX',
    'AT43', 'AT45', 'AT72', 'AT75', 'AT76', 'ATR',
    'DH8A', 'DH8B', 'DH8C', 'DH8D',
    'SF34', 'SB20', 'JS41', 'JS32', 'F50', 'F70', 'F100',
    'B190', 'BE99', 'BE20', 'BE30',
];

// Known airline callsign prefixes (3-letter ICAO codes) - these are ALWAYS commercial
const COMMERCIAL_AIRLINE_PREFIXES = [
    // US Airlines
    'AAL', 'UAL', 'DAL', 'SWA', 'JBU', 'ASA', 'NKS', 'FFT', 'HAL',
    // European Airlines
    'BAW', 'DLH', 'AFR', 'KLM', 'EZY', 'RYR', 'VIR', 'IBE', 'SAS', 'AUA', 'SWR', 'TAP', 'AZA', 'FIN',
    // Middle East
    'UAE', 'ETD', 'QTR', 'GFA', 'SVA', 'MEA', 'RJA', 'KAC', 'THY',
    // Asia-Pacific
    'SIA', 'CPA', 'QFA', 'JST', 'VOZ', 'ANZ', 'ANA', 'JAL', 'KAL', 'AAR', 'CAL', 'EVA', 'THA', 'MAS', 'GIA',
    // China
    'CCA', 'CES', 'CSN', 'HDA', 'CSC',
    // Canada
    'ACA', 'WJA', 'TSC',
    // Latin America
    'LAN', 'TAM', 'AVA', 'GLO', 'ARG', 'AMX', 'AEA',
    // Others
    'EIN', 'ICE', 'LOT', 'CSA', 'ROT', 'AEE', 'SAA', 'ETH',
];

// All countries with center coordinates for zooming
const COUNTRY_CENTERS = {
    "Afghanistan": { coordinates: [67, 33], zoom: 5 },
    "Albania": { coordinates: [20, 41], zoom: 8 },
    "Algeria": { coordinates: [3, 28], zoom: 3 },
    "Argentina": { coordinates: [-64, -34], zoom: 3 },
    "Australia": { coordinates: [134, -25], zoom: 3 },
    "Austria": { coordinates: [14, 48], zoom: 8 },
    "Bangladesh": { coordinates: [90, 24], zoom: 6 },
    "Belgium": { coordinates: [4, 51], zoom: 10 },
    "Brazil": { coordinates: [-52, -14], zoom: 2.5 },
    "Canada": { coordinates: [-106, 56], zoom: 2 },
    "Chile": { coordinates: [-71, -35], zoom: 3 },
    "China": { coordinates: [105, 35], zoom: 2.5 },
    "Colombia": { coordinates: [-74, 4], zoom: 4 },
    "Czech Republic": { coordinates: [15, 50], zoom: 8 },
    "Denmark": { coordinates: [10, 56], zoom: 7 },
    "Egypt": { coordinates: [30, 27], zoom: 5 },
    "Finland": { coordinates: [26, 64], zoom: 4 },
    "France": { coordinates: [2, 46], zoom: 5 },
    "Germany": { coordinates: [10, 51], zoom: 5 },
    "Greece": { coordinates: [22, 39], zoom: 6 },
    "Hong Kong": { coordinates: [114, 22], zoom: 11 },
    "Hungary": { coordinates: [20, 47], zoom: 7 },
    "Iceland": { coordinates: [-19, 65], zoom: 5 },
    "India": { coordinates: [79, 22], zoom: 3 },
    "Indonesia": { coordinates: [118, -2], zoom: 3 },
    "Iran": { coordinates: [53, 32], zoom: 4 },
    "Iraq": { coordinates: [44, 33], zoom: 5 },
    "Ireland": { coordinates: [-8, 53], zoom: 7 },
    "Israel": { coordinates: [35, 31], zoom: 8 },
    "Italy": { coordinates: [12, 42], zoom: 5 },
    "Japan": { coordinates: [138, 36], zoom: 4 },
    "Jordan": { coordinates: [36, 31], zoom: 7 },
    "Kenya": { coordinates: [38, 0], zoom: 5 },
    "Kuwait": { coordinates: [48, 29], zoom: 9 },
    "Lebanon": { coordinates: [36, 34], zoom: 9 },
    "Libya": { coordinates: [17, 27], zoom: 4 },
    "Malaysia": { coordinates: [109, 4], zoom: 4 },
    "Mexico": { coordinates: [-102, 24], zoom: 3 },
    "Morocco": { coordinates: [-6, 32], zoom: 5 },
    "Nepal": { coordinates: [84, 28], zoom: 6 },
    "Netherlands": { coordinates: [5, 52], zoom: 8 },
    "New Zealand": { coordinates: [172, -42], zoom: 4 },
    "Nigeria": { coordinates: [8, 10], zoom: 4 },
    "Norway": { coordinates: [10, 62], zoom: 4 },
    "Oman": { coordinates: [57, 21], zoom: 6 },
    "Pakistan": { coordinates: [69, 30], zoom: 4 },
    "Peru": { coordinates: [-76, -10], zoom: 4 },
    "Philippines": { coordinates: [122, 12], zoom: 4 },
    "Poland": { coordinates: [20, 52], zoom: 5 },
    "Portugal": { coordinates: [-8, 40], zoom: 6 },
    "Qatar": { coordinates: [51, 25], zoom: 9 },
    "Romania": { coordinates: [25, 46], zoom: 6 },
    "Russia": { coordinates: [100, 60], zoom: 1.5 },
    "Saudi Arabia": { coordinates: [45, 24], zoom: 4 },
    "Singapore": { coordinates: [104, 1], zoom: 11 },
    "South Africa": { coordinates: [25, -29], zoom: 4 },
    "South Korea": { coordinates: [128, 36], zoom: 6 },
    "Spain": { coordinates: [-4, 40], zoom: 5 },
    "Sri Lanka": { coordinates: [81, 8], zoom: 7 },
    "Sweden": { coordinates: [18, 62], zoom: 4 },
    "Switzerland": { coordinates: [8, 47], zoom: 8 },
    "Taiwan": { coordinates: [121, 24], zoom: 7 },
    "Thailand": { coordinates: [101, 15], zoom: 5 },
    "Turkey": { coordinates: [35, 39], zoom: 4 },
    "Ukraine": { coordinates: [32, 49], zoom: 4 },
    "United Arab Emirates": { coordinates: [54, 24], zoom: 7 },
    "United Kingdom": { coordinates: [-3, 54], zoom: 5 },
    "United States of America": { coordinates: [-98, 39], zoom: 2.5 },
    "Venezuela": { coordinates: [-66, 7], zoom: 4 },
    "Vietnam": { coordinates: [108, 16], zoom: 4 },
};

// Get sorted country list
const COUNTRY_LIST = Object.keys(COUNTRY_CENTERS).sort();

// Get aircraft size category - checks both aircraft type AND callsign
const getAircraftSize = (aircraftType, callsign) => {
    // First check aircraft type if available
    if (aircraftType) {
        const type = aircraftType.toUpperCase();
        if (LARGE_AIRCRAFT_LIST.includes(type)) return 'large';
        if (MEDIUM_AIRCRAFT_LIST.includes(type)) return 'medium';
    }
    
    // If no aircraft type, check if callsign indicates commercial airline
    if (callsign) {
        const cs = callsign.toUpperCase().trim();
        const prefix3 = cs.substring(0, 3);
        
        // If it's a known commercial airline prefix, treat as large aircraft
        if (COMMERCIAL_AIRLINE_PREFIXES.includes(prefix3)) {
            return 'large';
        }
        
        // Check 2-letter IATA-style callsigns (e.g., UA123, QF456)
        if (cs.length >= 3 && /^[A-Z]{2}\d/.test(cs)) {
            const prefix2 = cs.substring(0, 2);
            const commercialIATA = [
                'AA', 'UA', 'DL', 'WN', 'B6', 'AS', 'NK', 'F9', 'HA', // US
                'BA', 'LH', 'AF', 'KL', 'EK', 'QR', 'EY', 'SQ', 'CX', // Int'l
                'QF', 'VA', 'JQ', 'NZ', 'JL', 'NH', 'KE', 'OZ', 'CI', 'BR', // Asia-Pac
                'AC', 'WS', 'LA', 'AV', 'AM', 'IB', 'AZ', 'SK', 'AY', 'OS', // Other
                'FR', 'U2', 'W6', 'VY', 'DY', 'TK', 'MS', 'ET', 'SA', // LCC/Other
            ];
            if (commercialIATA.includes(prefix2)) {
                return 'large';
            }
        }
    }
    
    // Default to small for unknown aircraft
    return 'small';
};

// Determine flight type: domestic, international, or private
const getFlightType = (flight) => {
    const dep = flight.departure_iata;
    const arr = flight.arrival_iata;
    
    // If no route info, check aircraft size
    if (!dep || !arr) {
        const size = getAircraftSize(flight.aircraft_type, flight.callsign);
        return size === 'small' ? 'private' : 'unknown';
    }
    
    // Country airport codes for domestic detection
    const countryAirports = {
        'AU': ['SYD', 'MEL', 'BNE', 'PER', 'ADL', 'OOL', 'CNS', 'DRW', 'CBR', 'HBA', 'TSV', 'NTL', 'AVV', 'MCY', 'LST', 'ASP', 'AYQ', 'BME', 'HTI', 'PHE', 'KGI', 'ARM', 'ABX'],
        'NZ': ['AKL', 'WLG', 'CHC', 'ZQN', 'DUD', 'NPE', 'PMR', 'NSN', 'HLZ', 'ROT', 'NPL', 'TRG', 'IVC', 'BHE', 'GIS'],
        'US': ['JFK', 'LAX', 'ORD', 'DFW', 'DEN', 'SFO', 'SEA', 'ATL', 'MIA', 'BOS', 'PHX', 'IAH', 'LAS', 'MCO', 'EWR', 'MSP', 'DTW', 'PHL', 'CLT', 'SLC', 'SAN', 'TPA', 'PDX', 'STL', 'BWI', 'DCA', 'IAD', 'FLL', 'AUS', 'SMF', 'SNA', 'SJC', 'OAK', 'HNL'],
        'UK': ['LHR', 'LGW', 'STN', 'MAN', 'EDI', 'BHX', 'GLA', 'BRS', 'NCL', 'LTN', 'LBA', 'EMA', 'ABZ', 'BFS', 'LPL', 'SOU', 'CWL', 'INV'],
        'JP': ['NRT', 'HND', 'KIX', 'ITM', 'NGO', 'CTS', 'FUK', 'OKA', 'SDJ', 'HIJ', 'KOJ', 'KMJ', 'NGS', 'OIT', 'MYJ', 'TAK', 'AOJ'],
        'DE': ['FRA', 'MUC', 'DUS', 'BER', 'HAM', 'CGN', 'STR', 'HAJ', 'NUE', 'LEJ', 'DRS', 'BRE'],
        'FR': ['CDG', 'ORY', 'NCE', 'LYS', 'MRS', 'TLS', 'BOD', 'NTE', 'SXB', 'MPL', 'LIL'],
    };
    
    // Find which country each airport belongs to
    const getCountry = (iata) => {
        for (const [country, airports] of Object.entries(countryAirports)) {
            if (airports.includes(iata)) return country;
        }
        return null;
    };
    
    const depCountry = getCountry(dep);
    const arrCountry = getCountry(arr);
    
    // If both airports are in the same country = domestic
    if (depCountry && arrCountry && depCountry === arrCountry) {
        return 'domestic';
    }
    
    // If airports are in different countries = international
    if (depCountry && arrCountry && depCountry !== arrCountry) {
        return 'international';
    }
    
    // If we can identify at least one airport, guess based on callsign
    if (depCountry || arrCountry) {
        return 'international'; // Default to international if crossing borders
    }
    
    return 'unknown';
};

// Large plane SVG (commercial jets)
const LargePlaneIcon = ({ size = 16, color = "#ffffff", rotation = 0, isSelected = false }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        style={{
            transform: `rotate(${rotation}deg)`,
            filter: isSelected 
                ? 'drop-shadow(0 0 8px rgba(240, 112, 80, 0.9))' 
                : 'drop-shadow(0 0 3px rgba(255, 255, 255, 0.6))'
        }}
    >
        {/* Commercial jet silhouette */}
        <path
            d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
            fill={color}
        />
    </svg>
);

// Medium plane SVG (regional jets)
const MediumPlaneIcon = ({ size = 12, color = "#60a5fa", rotation = 0, isSelected = false }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        style={{
            transform: `rotate(${rotation}deg)`,
            filter: isSelected 
                ? 'drop-shadow(0 0 6px rgba(240, 112, 80, 0.8))' 
                : 'drop-shadow(0 0 2px rgba(96, 165, 250, 0.5))'
        }}
    >
        <path
            d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
            fill={color}
        />
    </svg>
);

// Small plane SVG (private/light aircraft) - proper plane shape
const SmallPlaneIcon = ({ size = 8, color = "#fbbf24", rotation = 0, isSelected = false }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        style={{
            transform: `rotate(${rotation}deg)`,
            filter: isSelected 
                ? 'drop-shadow(0 0 4px rgba(240, 112, 80, 0.8))' 
                : 'drop-shadow(0 0 1px rgba(251, 191, 36, 0.5))'
        }}
    >
        {/* Small single-engine plane silhouette */}
        <path
            d="M12 2c-.5 0-1 .5-1 1v6l-7 4v2l7-2v5l-2 1.5v1.5l3-1 3 1v-1.5l-2-1.5v-5l7 2v-2l-7-4V3c0-.5-.5-1-1-1z"
            fill={color}
        />
    </svg>
);

// Plane icon selector based on flight type
// White = Domestic, Yellow = International, Blue = Private
// Size scales inversely with zoom level
const PlaneIcon = ({ size = 12, rotation = 0, isSelected = false, flight = null, zoomLevel = 1 }) => {
    const flightType = flight ? getFlightType(flight) : 'unknown';
    const aircraftSize = getAircraftSize(flight?.aircraft_type, flight?.callsign);
    
    // Calculate dynamic size based on zoom level
    // More aggressive scaling for dense airspace areas
    // At zoom 1: smallest size (world view - prevent white blob)
    // At zoom 1-2: small size (world view)
    // At zoom 2-5: moderate size (country view)
    // At zoom 5-15: larger size (region view)
    // At zoom 15+: maximum size (city view)
    const zoomScale = zoomLevel <= 1 ? 0.5 :  // World view - smallest
                      zoomLevel <= 2 ? 0.65 :  // Zoomed out
                      zoomLevel <= 5 ? Math.max(0.5, 0.8 - (zoomLevel - 2) * 0.08) :
                      zoomLevel <= 15 ? Math.max(0.3, 0.5 - (zoomLevel - 5) * 0.02) :
                      0.25;
    
    // Determine color based on flight type
    // White (#ffffff) = Domestic flights
    // Yellow (#fbbf24) = International flights  
    // Blue (#60a5fa) = Private/small aircraft
    let color;
    let baseSize;
    
    if (aircraftSize === 'small' || flightType === 'private') {
        // Small private planes - Blue
        color = '#60a5fa';
        baseSize = Math.max(4, (size * 0.65) * zoomScale);
        return (
            <svg
                width={baseSize}
                height={baseSize}
                viewBox="0 0 24 24"
                fill="none"
                style={{
                    transform: `rotate(${rotation}deg)`,
                    filter: isSelected 
                        ? 'drop-shadow(0 0 4px rgba(240, 112, 80, 0.8))' 
                        : 'drop-shadow(0 0 2px rgba(96, 165, 250, 0.6))'
                }}
            >
                <path
                    d="M12 2c-.5 0-1 .5-1 1v6l-7 4v2l7-2v5l-2 1.5v1.5l3-1 3 1v-1.5l-2-1.5v-5l7 2v-2l-7-4V3c0-.5-.5-1-1-1z"
                    fill={color}
                />
            </svg>
        );
    } else if (flightType === 'international') {
        // International flights - Yellow
        color = '#fbbf24';
        baseSize = Math.max(5, size * zoomScale);
    } else {
        // Domestic flights and unknown - White
        color = '#ffffff';
        baseSize = Math.max(5, size * zoomScale);
    }
    
    return (
        <svg
            width={baseSize}
            height={baseSize}
            viewBox="0 0 24 24"
            fill="none"
            style={{
                transform: `rotate(${rotation}deg)`,
                filter: isSelected 
                    ? 'drop-shadow(0 0 8px rgba(240, 112, 80, 0.9))' 
                    : `drop-shadow(0 0 3px ${flightType === 'international' ? 'rgba(251, 191, 36, 0.7)' : 'rgba(255, 255, 255, 0.6)'})`
            }}
        >
            <path
                d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
                fill={color}
            />
        </svg>
    );
};

export const AzimuthalMap = ({ 
    flights = [], 
    selectedFlight, 
    onFlightSelect,
    isLoading = false,
    onRefresh,
    userCountry = null,
    searchFilter = '',  // Filter to show only matching flights
    onCountrySelect = null,  // Callback when country is selected on map
    flightTrack = null,  // Track data for selected flight (premium feature)
    isPremium = false  // Whether user is premium (for showing track)
}) => {
    const [tooltipContent, setTooltipContent] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState({ coordinates: [0, 0], zoom: 1 });
    const [selectedCountry, setSelectedCountry] = useState("");
    const [followFlight, setFollowFlight] = useState(false);
    const [showTrack, setShowTrack] = useState(true);  // Toggle for showing flight trail
    const followIntervalRef = useRef(null);

    // Follow Flight feature - auto-center on selected flight
    useEffect(() => {
        if (followFlight && selectedFlight && selectedFlight.longitude && selectedFlight.latitude) {
            // Center map on the selected flight
            setPosition(prev => ({
                ...prev,
                coordinates: [selectedFlight.longitude, selectedFlight.latitude],
                zoom: Math.max(prev.zoom, 5) // Zoom in if not already zoomed
            }));
        }
    }, [followFlight, selectedFlight, selectedFlight?.longitude, selectedFlight?.latitude]);

    // Auto-update position when following and flights refresh
    useEffect(() => {
        if (followFlight && selectedFlight) {
            // Find the updated flight data
            const updatedFlight = flights.find(f => f.icao24 === selectedFlight.icao24);
            if (updatedFlight && updatedFlight.longitude && updatedFlight.latitude) {
                setPosition(prev => ({
                    ...prev,
                    coordinates: [updatedFlight.longitude, updatedFlight.latitude]
                }));
            }
        }
    }, [flights, followFlight, selectedFlight]);

    // Stop following when flight is deselected
    useEffect(() => {
        if (!selectedFlight) {
            setFollowFlight(false);
        }
    }, [selectedFlight]);

    const handleToggleFollow = useCallback(() => {
        if (!selectedFlight) return;
        setFollowFlight(prev => !prev);
    }, [selectedFlight]);

    // Auto-zoom to user's country when it changes
    useEffect(() => {
        if (userCountry && COUNTRY_CENTERS[userCountry]) {
            const countryData = COUNTRY_CENTERS[userCountry];
            setPosition({
                coordinates: countryData.coordinates,
                zoom: countryData.zoom * 1.5
            });
            setSelectedCountry(userCountry);
        }
    }, [userCountry]);

    // Memoize flight markers
    const flightMarkers = useMemo(() => {
        let filtered = flights.filter(f => f.latitude && f.longitude);
        
        // If there's a search filter, only show matching flights
        if (searchFilter && searchFilter.trim().length >= 2) {
            const search = searchFilter.trim().toUpperCase();
            filtered = filtered.filter(f => {
                const callsign = (f.callsign || '').toUpperCase();
                const icao = (f.icao24 || '').toUpperCase();
                const airline = (f.airline_name || '').toUpperCase();
                return callsign.includes(search) || icao.includes(search) || airline.includes(search);
            });
        }
        
        return filtered.map(flight => ({
            ...flight,
            coordinates: [flight.longitude, flight.latitude],
            isSelected: selectedFlight?.icao24 === flight.icao24
        }));
    }, [flights, selectedFlight, searchFilter]);

    const handleMarkerClick = useCallback((flight) => {
        if (onFlightSelect) {
            onFlightSelect(flight);
        }
    }, [onFlightSelect]);

    const handleMarkerHover = useCallback((flight, event) => {
        if (flight && event) {
            setTooltipContent(flight);
            setTooltipPosition({ 
                x: event.clientX, 
                y: event.clientY 
            });
        } else {
            setTooltipContent(null);
        }
    }, []);

    const handleCountrySelect = useCallback((countryName) => {
        const countryData = COUNTRY_CENTERS[countryName];
        if (countryData) {
            setPosition({
                coordinates: countryData.coordinates,
                zoom: countryData.zoom * 1.5 // Higher zoom for Mercator view
            });
            setSelectedCountry(countryName);
            // Notify parent component
            if (onCountrySelect) {
                onCountrySelect(countryName);
            }
        }
    }, [onCountrySelect]);

    const handleCountryClick = useCallback((geo) => {
        const countryName = geo.properties.name;
        const countryData = COUNTRY_CENTERS[countryName];
        
        if (countryData) {
            setPosition({
                coordinates: countryData.coordinates,
                zoom: countryData.zoom * 1.5 // Higher zoom for Mercator view
            });
            setSelectedCountry(countryName);
            // Notify parent component
            if (onCountrySelect) {
                onCountrySelect(countryName);
            }
        }
    }, [onCountrySelect]);

    const handleReset = useCallback(() => {
        setPosition({ coordinates: [0, 0], zoom: 1 });
        setSelectedCountry("");
        // Notify parent to clear country filter
        if (onCountrySelect) {
            onCountrySelect("");
        }
    }, [onCountrySelect]);

    const handleZoomIn = useCallback(() => {
        if (position.zoom < 20) {
            setPosition(prev => ({ ...prev, zoom: prev.zoom * 1.5 }));
        }
    }, [position.zoom]);

    const handleZoomOut = useCallback(() => {
        if (position.zoom > 0.5) {
            setPosition(prev => ({ ...prev, zoom: prev.zoom / 1.5 }));
        }
    }, [position.zoom]);

    const handleMoveEnd = useCallback((newPosition) => {
        setPosition(newPosition);
    }, []);

    // Determine projection based on whether a country is selected
    const useStandardMap = selectedCountry !== "";
    
    // Use higher resolution map when zoomed in
    const geoUrl = position.zoom > 2 || useStandardMap ? GEO_URL_HIGH : GEO_URL_LOW;
    
    // Get projection config based on view mode
    const projectionConfig = useMemo(() => {
        if (useStandardMap) {
            // Standard Mercator for country view - like airnavradar.com
            return {
                scale: 150,
                center: position.coordinates
            };
        }
        // Azimuthal Equidistant for world view - like UN logo
        return {
            rotate: [0, -90, 0],
            scale: 180,
            center: [0, 0]
        };
    }, [useStandardMap, position.coordinates]);

    return (
        <div className="relative w-full h-full bg-[#071D35] rounded-xl border border-[#334155] overflow-hidden">
            {/* Map Header */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-3 flex-wrap">
                <div className="glass-card px-3 py-2 rounded-lg">
                    <span className="text-xs text-[#64748b] font-mono uppercase tracking-wider">
                        {useStandardMap ? 'Standard Map' : 'Azimuthal Equidistant'}
                    </span>
                </div>
                <div className="glass-card px-3 py-2 rounded-lg flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-xs text-[#94a3b8] font-mono">
                        {flights.length} Commercial Flights
                    </span>
                </div>
            </div>

            {/* Country Selector */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="glass-card p-2 rounded-lg flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#f07050]" />
                    <Select value={selectedCountry} onValueChange={handleCountrySelect}>
                        <SelectTrigger className="w-[200px] bg-[#071D35] border-[#334155] text-white text-sm">
                            <SelectValue placeholder="Select a country..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1e293b] border-[#334155] max-h-[300px]">
                            {COUNTRY_LIST.map(country => (
                                <SelectItem 
                                    key={country} 
                                    value={country}
                                    className="text-white hover:bg-[#334155] cursor-pointer"
                                >
                                    {country}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {selectedCountry && (
                        <Button
                            onClick={handleReset}
                            variant="ghost"
                            size="sm"
                            className="text-[#64748b] hover:text-white px-2"
                        >
                            <RotateCcw className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* Zoom Controls */}
            <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                {onRefresh && (
                    <Button
                        onClick={onRefresh}
                        disabled={isLoading}
                        variant="ghost"
                        size="icon"
                        className="glass-card hover:bg-[#334155]/50"
                        data-testid="map-refresh-btn"
                    >
                        <RefreshCw className={`h-5 w-5 text-[#94a3b8] ${isLoading ? 'animate-spin' : ''}`} />
                    </Button>
                )}
                {/* Follow Flight Button */}
                {selectedFlight && (
                    <Button
                        onClick={handleToggleFollow}
                        variant="ghost"
                        size="icon"
                        className={`glass-card hover:bg-[#334155]/50 ${followFlight ? 'bg-[#f07050]/20 border border-[#f07050]' : ''}`}
                        data-testid="follow-flight-btn"
                        title={followFlight ? "Stop following flight" : "Follow selected flight"}
                    >
                        <Crosshair className={`h-5 w-5 ${followFlight ? 'text-[#f07050] animate-pulse' : 'text-[#94a3b8]'}`} />
                    </Button>
                )}
                {/* Show Trail Button - Premium Only */}
                {isPremium && selectedFlight && flightTrack && flightTrack.length > 0 && (
                    <Button
                        onClick={() => setShowTrack(prev => !prev)}
                        variant="ghost"
                        size="icon"
                        className={`glass-card hover:bg-[#334155]/50 ${showTrack ? 'bg-[#f07050]/20 border border-[#f07050]' : ''}`}
                        data-testid="show-trail-btn"
                        title={showTrack ? "Hide flight trail" : "Show flight trail"}
                    >
                        <Route className={`h-5 w-5 ${showTrack ? 'text-[#f07050]' : 'text-[#94a3b8]'}`} />
                    </Button>
                )}
                <Button
                    onClick={handleZoomIn}
                    variant="ghost"
                    size="icon"
                    className="glass-card hover:bg-[#334155]/50"
                    data-testid="zoom-in-btn"
                >
                    <ZoomIn className="h-5 w-5 text-[#94a3b8]" />
                </Button>
                <Button
                    onClick={handleZoomOut}
                    variant="ghost"
                    size="icon"
                    className="glass-card hover:bg-[#334155]/50"
                    data-testid="zoom-out-btn"
                >
                    <ZoomOut className="h-5 w-5 text-[#94a3b8]" />
                </Button>
                <Button
                    onClick={handleReset}
                    variant="ghost"
                    size="icon"
                    className="glass-card hover:bg-[#334155]/50"
                    data-testid="zoom-reset-btn"
                >
                    <RotateCcw className="h-5 w-5 text-[#94a3b8]" />
                </Button>
            </div>

            {/* Map */}
            <ComposableMap
                projection={useStandardMap ? "geoMercator" : "geoAzimuthalEquidistant"}
                projectionConfig={projectionConfig}
                className="azimuthal-map"
                style={{ width: '100%', height: '100%' }}
            >
                <ZoomableGroup
                    center={position.coordinates}
                    zoom={position.zoom}
                    onMoveEnd={handleMoveEnd}
                    minZoom={0.5}
                    maxZoom={50}
                >
                    {/* Graticule (grid lines) */}
                    <Graticule 
                        stroke="#334155" 
                        strokeWidth={0.3}
                        step={useStandardMap ? [5, 5] : [15, 15]}
                    />

                    {/* Countries */}
                    <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const isSelected = selectedCountry === geo.properties.name;
                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        onClick={() => handleCountryClick(geo)}
                                        fill={isSelected ? '#2d4a3e' : '#1e3a2f'}
                                        stroke={isSelected ? '#f07050' : '#3d5a4f'}
                                        strokeWidth={isSelected ? 1.5 : 0.5}
                                        style={{
                                            default: { outline: 'none', cursor: 'pointer' },
                                            hover: { fill: '#3d5a4f', outline: 'none', cursor: 'pointer' },
                                            pressed: { outline: 'none' }
                                        }}
                                    />
                                );
                            })
                        }
                    </Geographies>

                    {/* US States - Show when zoomed into USA */}
                    {selectedCountry === "United States of America" && (
                        <Geographies geography={GEO_URL_US_STATES}>
                            {({ geographies }) =>
                                geographies.map((geo) => (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill="transparent"
                                        stroke="#4d6a5f"
                                        strokeWidth={0.3}
                                        style={{
                                            default: { outline: 'none' },
                                            hover: { outline: 'none' },
                                            pressed: { outline: 'none' }
                                        }}
                                    />
                                ))
                            }
                        </Geographies>
                    )}

                    {/* Australian States - Show when zoomed into Australia */}
                    {selectedCountry === "Australia" && (
                        <Geographies geography={GEO_URL_AU_STATES}>
                            {({ geographies }) =>
                                geographies.map((geo) => (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill="transparent"
                                        stroke="#4d6a5f"
                                        strokeWidth={0.4}
                                        style={{
                                            default: { outline: 'none' },
                                            hover: { outline: 'none' },
                                            pressed: { outline: 'none' }
                                        }}
                                    />
                                ))
                            }
                        </Geographies>
                    )}

                    {/* Flight Trail - Premium Feature */}
                    {isPremium && showTrack && flightTrack && flightTrack.length >= 2 && selectedFlight && (
                        <>
                            {/* Draw trail as connected line segments with gradient effect */}
                            {flightTrack.slice(0, -1).map((point, index) => {
                                const nextPoint = flightTrack[index + 1];
                                // Calculate opacity: older points are more transparent
                                const opacity = 0.3 + (0.7 * (index / flightTrack.length));
                                // Calculate stroke width: older points are thinner
                                const strokeWidth = 1 + (2 * (index / flightTrack.length));
                                
                                return (
                                    <Line
                                        key={`track-${index}`}
                                        from={[point.longitude, point.latitude]}
                                        to={[nextPoint.longitude, nextPoint.latitude]}
                                        stroke="#f07050"
                                        strokeWidth={strokeWidth / position.zoom}
                                        strokeOpacity={opacity}
                                        strokeLinecap="round"
                                    />
                                );
                            })}
                            
                            {/* Trail start marker (oldest point) */}
                            <Marker coordinates={[flightTrack[0].longitude, flightTrack[0].latitude]}>
                                <circle
                                    r={3 / position.zoom}
                                    fill="#f07050"
                                    fillOpacity={0.5}
                                    stroke="#fff"
                                    strokeWidth={0.5 / position.zoom}
                                />
                            </Marker>
                        </>
                    )}

                    {/* Flight Markers */}
                    {flightMarkers.map((flight) => {
                        // Calculate bearing to destination if we have arrival airport
                        let rotation = (flight.heading || 0) - 90; // Default: use heading
                        
                        if (flight.arrival_iata && flight.latitude && flight.longitude) {
                            const bearingToDest = calculateBearingToDestination(
                                flight.latitude, 
                                flight.longitude, 
                                flight.arrival_iata
                            );
                            if (bearingToDest !== null) {
                                rotation = bearingToDest - 90; // Adjust for SVG orientation
                            }
                        }
                        
                        return (
                            <Marker
                                key={flight.icao24}
                                coordinates={flight.coordinates}
                                onClick={() => handleMarkerClick(flight)}
                                onMouseEnter={(e) => handleMarkerHover(flight, e)}
                                onMouseLeave={() => handleMarkerHover(null, null)}
                            >
                                <g 
                                    className="cursor-pointer"
                                    data-testid={`flight-marker-${flight.icao24}`}
                                >
                                    <PlaneIcon
                                        size={flight.isSelected ? 16 : 12}
                                        rotation={rotation}
                                        isSelected={flight.isSelected}
                                        flight={flight}
                                        zoomLevel={position.zoom}
                                    />
                                </g>
                            </Marker>
                        );
                    })}
                </ZoomableGroup>
            </ComposableMap>

            {/* Tooltip */}
            {tooltipContent && (
                <div
                    className="fixed z-50 glass-card px-3 py-2 rounded-lg pointer-events-none"
                    style={{
                        left: tooltipPosition.x + 10,
                        top: tooltipPosition.y - 40,
                    }}
                >
                    <div className="text-sm font-bold text-white">
                        {tooltipContent.callsign || tooltipContent.icao24}
                    </div>
                    <div className="text-xs text-[#94a3b8]">
                        {tooltipContent.origin_country} · {tooltipContent.aircraft_type || 'Unknown'}
                    </div>
                    <div className="text-xs text-[#64748b]">
                        {tooltipContent.altitude_feet?.toLocaleString()} ft · {tooltipContent.velocity_knots?.toFixed(0)} kts
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {isLoading && (
                <div className="absolute inset-0 bg-[#071D35]/50 flex items-center justify-center">
                    <div className="glass-card px-6 py-4 rounded-lg flex items-center gap-3">
                        <RefreshCw className="h-5 w-5 text-[#f07050] animate-spin" />
                        <span className="text-sm text-white">Loading flights...</span>
                    </div>
                </div>
            )}

            {/* Instructions */}
            <div className="absolute bottom-4 right-4 z-10">
                <div className="glass-card px-3 py-2 rounded-lg text-xs text-[#64748b]">
                    Click country to zoom · Drag to pan · Scroll to zoom
                </div>
            </div>

            {/* Follow Flight indicator */}
            {followFlight && selectedFlight && (
                <div className="absolute bottom-4 left-4 z-10">
                    <div className="glass-card px-3 py-2 rounded-lg flex items-center gap-2 border border-[#f07050]/50 bg-[#f07050]/10">
                        <Crosshair className="h-4 w-4 text-[#f07050] animate-pulse" />
                        <span className="text-sm text-white">Following: <span className="font-mono font-bold text-[#f07050]">{selectedFlight.callsign || selectedFlight.icao24}</span></span>
                        <button 
                            onClick={handleToggleFollow}
                            className="ml-2 text-xs text-[#94a3b8] hover:text-white underline"
                        >
                            Stop
                        </button>
                    </div>
                </div>
            )}

            {/* Viewing indicator (only show if not following a flight) */}
            {selectedCountry && !followFlight && (
                <div className="absolute bottom-4 left-4 z-10">
                    <div className="glass-card px-3 py-2 rounded-lg flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#f07050]" />
                        <span className="text-sm text-white">{selectedCountry}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
